import { axiosInstance } from "@/pages/auth/auth.context";
import {
  PartnerDto,
  CreatePartnerRequest,
  UpdatePartnerRequest,
} from "@/types/partner";

export interface PartnerValidationResponse {
  valid: boolean;
  referralCode?: string;
  companyName?: string;
  contactName?: string;
  status?: string;
  message?: string;
  partner?: PartnerDto;
}

export const partnerService = {
  async getAllPartners(): Promise<PartnerDto[]> {
    const response = await axiosInstance.get("partners");
    return response.data;
  },

  async getPartnerById(id: string): Promise<PartnerDto> {
    const response = await axiosInstance.get(`partners/${id}`);
    return response.data;
  },

  async createPartner(partner: CreatePartnerRequest): Promise<PartnerDto> {
    const response = await axiosInstance.post("partners", partner);
    return response.data;
  },

  async updatePartner(
    id: string,
    partner: UpdatePartnerRequest,
  ): Promise<PartnerDto> {
    const response = await axiosInstance.put(`partners/${id}`, partner);
    return response.data;
  },

  async deletePartner(id: string): Promise<void> {
    await axiosInstance.delete(`partners/${id}`);
  },

  async validateReferralCode(code: string): Promise<PartnerValidationResponse> {
    if (!code || code.trim() === "") {
      return { valid: true, partner: undefined };
    }

    try {
      const response = await axiosInstance.get(
        `partners/validate/${encodeURIComponent(code)}`,
      );

      // Map backend response to our expected format
      const backendData = response.data;

      const partnerDto: PartnerDto | undefined = backendData.valid
        ? {
            id: backendData.id,
            referralCode: backendData.referralCode,
            companyName: backendData.companyName,
            contactName: backendData.contactName,
            email: "", // Not available from validation endpoint
            phone: null,
            address: null,
            referralPercentage: 0, // Default value
            status: (backendData.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
            taxId: null,
            notes: null,
          }
        : undefined;

      return {
        valid: backendData.valid,
        referralCode: backendData.referralCode,
        companyName: backendData.companyName,
        contactName: backendData.contactName,
        status: backendData.status,
        message: backendData.message,
        partner: partnerDto, // For backward compatibility
      };
    } catch (error) {
      console.error("Referral code validation failed:", error);

      // Handle 404 specifically for invalid codes
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response: {
            status: number;
            data?: { referralCode?: string; message?: string };
          };
        };
        if (axiosError.response?.status === 404) {
          const errorData = axiosError.response.data;
          return {
            valid: false,
            referralCode: errorData?.referralCode || code,
            message: errorData?.message || "Partner referral code not found",
            partner: undefined,
          };
        }
      }

      // Handle other errors
      return {
        valid: false,
        partner: undefined,
        message: "Error validating referral code",
      };
    }
  },
};
